import { makeStyles, tokens } from '@fluentui/react-components'

// Subtle scale+fade entrance for send/undo button swap
const buttonFadeIn = {
  from: { opacity: 0, transform: 'scale(0.85)' },
  to: { opacity: 1, transform: 'scale(1)' },
}

// Gradient border animation: shifts background-position on an oversized pseudo-element.
// No rotation, no transform — the element shape never moves.
const iMatchShift = {
  from: { backgroundPosition: '0% 0%' },
  to: { backgroundPosition: '200% 0%' },
}

// The composer is a chain of nested flex columns (chatBoxBody > editorWrapper > the
// Trumbowyg-generated boxes), and every link in that chain needs this same shape to let
// content shrink instead of overflowing or pushing its parent taller. flexShrink/minHeight
// don't inherit down the tree, so each layer has to declare it independently
const flexFillColumn = {
  display: 'flex',
  flexDirection: 'column',
  flexGrow: 1,
  flexShrink: 1,
  flexBasis: 'auto',
  minHeight: 0,
  overflow: 'hidden',
} as const

export const useStyles = makeStyles({
  chatBox: {
    position: 'relative',
    backgroundColor: tokens.colorNeutralBackground1,
    borderRadius: tokens.borderRadius3XLarge,
    boxShadow: tokens.shadow4,
    width: '100%',
    boxSizing: 'border-box',
    display: 'flex',
    flexDirection: 'column',
    maxHeight: 'var(--chat-box-max-height, none)',
    // ::before is always present (opacity: 0) so CSS can transition it in/out smoothly.
    // The element's own background clips the interior — only the 2px ring is visible.
    // Offset is 4px (not 2px) so the active state can insert a 2px background-color gap via
    // box-shadow, keeping the ring clearly outset rather than flush with the chatBox edge.
    '&::before': {
      content: '""',
      position: 'absolute',
      left: '-4px',
      top: '-4px',
      width: 'calc(100% + 8px)',
      height: 'calc(100% + 8px)',
      // 16px (borderRadius3XLarge) + 4px border extension
      borderRadius: `calc(${tokens.borderRadius3XLarge} + 4px)`,
      background: 'linear-gradient(45deg, #00BCFF, #89D329)',
      backgroundSize: '200%',
      zIndex: -1,
      opacity: 0,
      transitionProperty: 'opacity',
      transitionDuration: '0.6s',
      transitionTimingFunction: 'ease',
    },
  },
  // Animation only runs when active so it starts fresh on fade-in.
  // Fade-out shows the static gradient dissolving away — no abrupt cut.
  // The spread-only shadow (0 0 0 2px background1) creates a 2px background-colored gap
  // between the chatBox edge and the gradient ring, so the ring reads as outset decoration
  // rather than an inset border — preventing the perceived shrink.
  chatBoxImatchBorder: {
    boxShadow: `0 0 0 2px ${tokens.colorNeutralBackground1}, ${tokens.shadow16}`,
    '&::before': {
      opacity: 1,
      animationName: iMatchShift,
      animationDuration: '1s',
      animationTimingFunction: 'linear',
      animationIterationCount: 'infinite',
    },
  },
  // Flex column for context + input + action bar. Overflow is visible (see below) so the slash
  // menu can escape; the resize handle also sits outside this wrapper.
  chatBoxBody: {
    ...flexFillColumn,
    // Visible, not hidden: the slash menu opens upward from the editor and must overlay the
    // context bar and extend past the composer. Children size themselves (allocated heights).
    overflow: 'visible',
  },
  // Rigid on purpose: context renders at exactly the height ChatInput allocated it (see the
  // COMPOSER SIZING comment there), and never gets squeezed by the browser on top of that.
  // Leaving the input region as the composer's only flexible child is what makes the two
  // regions trade space cleanly — one grows, the other yields the same pixels, one layout pass.
  contextSection: {
    position: 'relative',
    display: 'flex',
    flexDirection: 'column',
    flexGrow: 0,
    flexShrink: 0,
    flexBasis: 'auto',
    minHeight: 0,
  },
  resizeHandle: {
    position: 'absolute',
    top: tokens.spacingVerticalXS,
    left: 0,
    width: '100%',
    height: tokens.spacingVerticalM,
    border: 'none',
    backgroundColor: 'transparent',
    padding: 0,
    margin: 0,
    cursor: 'ns-resize',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    transform: 'translateY(-50%)',
    zIndex: 1,
    borderTopLeftRadius: tokens.borderRadiusLarge,
    borderTopRightRadius: tokens.borderRadiusLarge,
    ':focus-visible': {
      outlineStyle: 'solid',
      outlineWidth: tokens.strokeWidthThick,
      outlineColor: tokens.colorStrokeFocus2,
    },
  },
  // Visibility is driven from JS (hover/focus of the handle itself or the context region),
  // not CSS :hover/:focus-within — the handle button lives outside the clipped chatBoxBody
  // (see chatBoxBody comment), so it has no shared ancestor with contextSection for pure
  // CSS descendant selectors to react to hovering/focusing context controls.
  resizeHandleGrip: {
    display: 'block',
    width: `calc(${tokens.spacingHorizontalXXXL} + ${tokens.spacingHorizontalXS})`,
    height: tokens.spacingVerticalXS,
    borderRadius: tokens.borderRadiusCircular,
    backgroundColor: tokens.colorNeutralStroke2,
    opacity: 0,
    transitionDuration: tokens.durationNormal,
    transitionProperty: 'opacity',
    transitionTimingFunction: tokens.curveEasyEase,
  },
  resizeHandleGripVisible: {
    opacity: 1,
  },
  editorWrapper: {
    ...flexFillColumn,
    // Anchors PromptSlashMenu; overflow must stay visible so the menu isn't clipped.
    overflow: 'visible',
    position: 'relative',
  },
  inputWrapper: {
    padding: `${tokens.spacingVerticalSNudge} ${tokens.spacingHorizontalS} 0`,
    display: 'flex',
    flexDirection: 'column',
    boxSizing: 'border-box',
    width: '100%',
    // May shrink below content height once the composer hits its max-height ceiling.
    flexGrow: 0,
    flexShrink: 1,
    flexBasis: 'auto',
    minHeight: 0,
    overflow: 'visible',
    '& .trumbowyg-box': {
      ...flexFillColumn,
      width: '100%',
      border: 'none',
      margin: tokens.spacingVerticalNone,
    },
    '& .trumbowyg-editor-box': {
      ...flexFillColumn,
      width: '100%',
    },
    '& .trumbowyg-button-pane': {
      display: 'none',
    },
    '& .trumbowyg-editor': {
      width: '100%',
      boxSizing: 'border-box',
      overflowY: 'auto',
      fontSize: tokens.fontSizeBase300,
      lineHeight: tokens.lineHeightBase300,
      fontFamily: tokens.fontFamilyBase,
      fontWeight: tokens.fontWeightRegular,
      flexGrow: 1,
      flexShrink: 1,
      flexBasis: 'auto',
      minHeight: `calc(${tokens.lineHeightBase300} + ${tokens.lineHeightBase300})`,
      padding: `0 ${tokens.spacingHorizontalXXS}`,
      margin: tokens.spacingVerticalNone,
      border: 'none',
      backgroundColor: 'transparent',
      textAlign: 'left',
      cursor: 'text',
      position: 'relative',
    },
    '& .trumbowyg-editor p': {
      margin: tokens.spacingVerticalNone,
      padding: tokens.spacingVerticalNone,
    },
  },
  placeholderOverlay: {
    position: 'absolute',
    top: 0,
    left: tokens.spacingHorizontalXXS,
    right: tokens.spacingHorizontalXXS,
    pointerEvents: 'none',
    color: tokens.colorNeutralForeground3,
    '& kbd': {
      display: 'inline-flex',
      alignItems: 'center',
      padding: `0 ${tokens.spacingHorizontalXS}`,
      borderRadius: tokens.borderRadiusMedium,
      border: `${tokens.strokeWidthThin} solid ${tokens.colorNeutralStroke1}`,
      color: tokens.colorNeutralForeground1,
    },
  },
  actionBar: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: `${tokens.spacingVerticalSNudge} ${tokens.spacingHorizontalS} ${tokens.spacingVerticalS}`,
    gap: tokens.spacingHorizontalXS,
    flexShrink: 0,
  },
  leftActions: {
    display: 'flex',
    gap: tokens.spacingHorizontalXS,
    alignItems: 'center',
  },
  libraryLink: {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: tokens.spacingHorizontalXXXL,
    height: tokens.spacingHorizontalXXXL,
    borderRadius: tokens.borderRadiusCircular,
    color: tokens.colorNeutralForeground2,
    fontSize: tokens.fontSizeBase500,
    textDecoration: 'none',
    ':hover': {
      backgroundColor: tokens.colorSubtleBackgroundHover,
      color: tokens.colorNeutralForeground2BrandHover,
    },
    ':active': {
      backgroundColor: tokens.colorSubtleBackgroundPressed,
      color: tokens.colorNeutralForeground2BrandPressed,
    },
    ':focus-visible': {
      outlineStyle: 'solid',
      outlineWidth: tokens.strokeWidthThick,
      outlineColor: tokens.colorStrokeFocus2,
      outlineOffset: tokens.strokeWidthThin,
    },
  },
  middleActions: {
    display: 'flex',
    alignItems: 'center',
    marginLeft: 'auto',
    marginRight: tokens.spacingHorizontalXXS,
  },
  modelMenuButton: {
    minWidth: `calc(${tokens.spacingHorizontalXXXL} * 3)`,
    height: tokens.spacingHorizontalXXL,
    padding: `${tokens.spacingVerticalXXS} ${tokens.spacingHorizontalS}`,
    borderRadius: tokens.borderRadiusCircular,
    color: tokens.colorNeutralForeground2,
    fontSize: tokens.fontSizeBase200,
    lineHeight: tokens.lineHeightBase200,
    fontWeight: tokens.fontWeightRegular,
    border: 'none',
    boxShadow: 'none',
    backgroundColor: 'transparent',
    '&:hover': {
      backgroundColor: tokens.colorSubtleBackgroundHover,
    },
    '&:active': {
      backgroundColor: tokens.colorSubtleBackgroundPressed,
    },
    '&:focus-visible': {
      outlineStyle: 'solid',
      outlineWidth: tokens.strokeWidthThick,
      outlineColor: tokens.colorStrokeFocus2,
      outlineOffset: tokens.strokeWidthThin,
    },
  },
  undoButton: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    padding: `${tokens.spacingVerticalSNudge} ${tokens.spacingHorizontalSNudge}`,
    borderRadius: tokens.borderRadiusCircular,
    backgroundColor: tokens.colorBrandBackground2,
    animationName: buttonFadeIn,
    animationDuration: tokens.durationNormal,
    animationTimingFunction: tokens.curveDecelerateMin,
    ':hover': {
      backgroundColor: tokens.colorBrandBackground2Hover,
    },
    ':active': {
      backgroundColor: tokens.colorBrandBackground2Pressed,
    },
    width: tokens.spacingHorizontalXXXL,
    height: tokens.spacingHorizontalXXXL,
  },
  sendButton: {
    animationName: buttonFadeIn,
    animationDuration: tokens.durationNormal,
    animationTimingFunction: tokens.curveDecelerateMin,
    width: tokens.spacingHorizontalXXXL,
    height: tokens.spacingHorizontalXXXL,
  },
  saveSpinner: {
    width: tokens.spacingHorizontalXXXL,
    height: tokens.spacingHorizontalXXXL,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
})
